import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { ClosedPeriod } from "./models/ClosedPeriod";
import {
  createClosedPeriod,
  deleteClosedPeriod,
  fetchClosedPeriods,
  PaginatedClosedPeriods,
  updateClosedPeriod,
} from "./slice-extensions/closed-periods-slice-extension";

interface ClosedPeriodsState {
  items: ClosedPeriod[];
  page: number;
  pageSize: number;
  totalCount: number;
  status: LoadingStatus;
  saveStatus: LoadingStatus;
  lastError?: ApiErrorResponse;
}

const initialState: ClosedPeriodsState = {
  items: [],
  page: 1,
  pageSize: 20,
  totalCount: 0,
  status: LoadingStatus.Idle,
  saveStatus: LoadingStatus.Idle,
  lastError: undefined,
};

const upsert = (items: ClosedPeriod[], updated: ClosedPeriod): ClosedPeriod[] =>
  items.some((i) => i.id === updated.id)
    ? items.map((i) => (i.id === updated.id ? updated : i))
    : [updated, ...items];

const closedPeriodsSlice = createSlice({
  name: "closedPeriods",
  initialState,
  reducers: {
    clearClosedPeriodsError: (state) => {
      state.lastError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClosedPeriods.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchClosedPeriods.fulfilled, (state, action: PayloadAction<PaginatedClosedPeriods>) => {
        state.status = LoadingStatus.Idle;
        state.items = action.payload.items;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchClosedPeriods.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(createClosedPeriod.pending, (state) => {
        state.saveStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(createClosedPeriod.fulfilled, (state, action: PayloadAction<ClosedPeriod>) => {
        state.saveStatus = LoadingStatus.Idle;
        state.items = [action.payload, ...state.items];
      })
      .addCase(createClosedPeriod.rejected, (state, action) => {
        state.saveStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(updateClosedPeriod.fulfilled, (state, action: PayloadAction<ClosedPeriod>) => {
        state.items = upsert(state.items, action.payload);
      })
      .addCase(updateClosedPeriod.rejected, (state, action) => {
        state.lastError = action.payload;
      })

      .addCase(deleteClosedPeriod.fulfilled, (state, action: PayloadAction<string>) => {
        state.items = state.items.filter((cp) => cp.id !== action.payload);
      })
      .addCase(deleteClosedPeriod.rejected, (state, action) => {
        state.lastError = action.payload;
      });
  },
});

export const { clearClosedPeriodsError } = closedPeriodsSlice.actions;

export const selectClosedPeriods = (state: RootState) => state.closedPeriods.items;
export const selectClosedPeriodsError = (state: RootState) => state.closedPeriods.lastError;

export default closedPeriodsSlice.reducer;
